import API from '../config/api';
import React, { useEffect, useState } from 'react';

import axios from 'axios';

import { Container, Row, Col, Card, Form } from 'react-bootstrap';

import { toast } from 'react-toastify';

import CustomNavbar from '../components/CustomNavbar';

/* One place where a refund status becomes a colour and an icon. Everything
   tinted on a card — top strip, pill, avatar, hover glow — reads from here. */
const REFUND_STATES = {
  Pending: { icon: '⏳', from: '#b45309', to: '#fcd34d', solid: '#f59e0b' },
  Approved: { icon: '✓', from: '#15803d', to: '#4ade80', solid: '#22c55e' },
  Rejected: { icon: '✕', from: '#991b1b', to: '#f87171', solid: '#ef4444' },
  Refunded: { icon: '💸', from: '#1e40af', to: '#60a5fa', solid: '#3b82f6' }
};

const stateOf = (status) => REFUND_STATES[status] || REFUND_STATES.Pending;

const DAY = 1000 * 60 * 60 * 24;

/* A refund left pending is money a customer is still waiting on, so the
   waiting chip warms up the longer it sits. */
const ageToneOf = (days) => {
  if (days >= 5) return 'is-hot';
  if (days >= 2) return 'is-warm';

  return 'is-cool';
};

const DECISIONS = [
  { status: 'Approved', idle: '✓ Approve', done: '✓ Approved', key: 'ok' },
  { status: 'Rejected', idle: '✕ Reject', done: '✕ Rejected', key: 'no' },
  { status: 'Refunded', idle: '💸 Refund', done: '💸 Refunded', key: 'paid' }
];

/* Reaching one of these ends the request for good — every further decision is
   locked, and the 5-day auto-delete (TTL index on the Refund model) starts. */
const FINAL_STATUSES = ['Rejected', 'Refunded'];

const AUTO_DELETE_DAYS = 5;

/* Which buttons stay live. Mirrored by canTransition() in refundController —
   keep the two in step. */
const canTransition = (from, to) => {
  if (from === to) return false;

  /* Rejected and Refunded are both terminal — the decision is made. */
  if (FINAL_STATUSES.includes(from)) return false;

  /* Once approved, the request cannot be rejected; it can only be paid out. */
  if (from === 'Approved' && to === 'Rejected') return false;

  return true;
};

/* Days until a settled request is auto-deleted, or null if it is still open */
const daysUntilDelete = (refund) => {
  if (!FINAL_STATUSES.includes(refund.status) || !refund.settledAt) {
    return null;
  }

  const elapsed = (Date.now() - new Date(refund.settledAt).getTime()) / DAY;

  return Math.max(0, Math.ceil(AUTO_DELETE_DAYS - elapsed));
};

function AdminRefundsPage() {
  const [refunds, setRefunds] = useState([]);

  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');

  const [statusFilter, setStatusFilter] = useState('All');

  const fetchRefunds = async () => {
    try {
      const { data } = await axios.get(`${API}/refunds`);

      setRefunds(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefunds();
  }, []);

  const updateStatus = async (
    id,

    status
  ) => {
    try {
      await axios.put(
        `${API}/refunds/${id}`,

        {
          status
        }
      );

      toast.success(
        FINAL_STATUSES.includes(status)
          ? `Marked ${status} — auto-deletes in ${AUTO_DELETE_DAYS} days`
          : `Marked ${status}`
      );

      fetchRefunds();
    } catch (error) {
      console.log(error);

      /* The API rejects transitions the UI already blocks (409); surface the
         reason rather than failing silently on a stale page. */
      toast.error(
        error.response?.data?.message || 'Failed to update refund status'
      );
    }
  };

  if (loading) {
    return (
      <>
        <CustomNavbar />

        <div className="arf-wrap">
          <Container className="text-center py-5">
            <div className="arf-loader" aria-hidden="true">
              <span className="arf-loader-ring" />
              <span className="arf-loader-coin">💰</span>
            </div>
            <p className="arf-loader-text">Gathering refund requests…</p>
          </Container>
        </div>

        <style>{refundsStyles}</style>
      </>
    );
  }

  const countOf = (status) =>
    status === 'All'
      ? refunds.length
      : refunds.filter((r) => r.status === status).length;

  const filteredRefunds = refunds.filter((refund) => {
    const keyword = search.toLowerCase();
    const matchesSearch =
      refund.userName?.toLowerCase().includes(keyword) ||
      refund.productName?.toLowerCase().includes(keyword) ||
      refund.orderNumber?.toLowerCase().includes(keyword);
    const matchesStatus =
      statusFilter === 'All' || refund.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filters = ['All', 'Pending', 'Approved', 'Rejected', 'Refunded'];

  return (
    <>
      <CustomNavbar />

      <div className="arf-wrap">
        <Container>
          {/* Header */}
          <Card className="border-0 shadow-lg mb-4 arf-hero">
            <span className="arf-hero-aurora" aria-hidden="true" />
            <span className="arf-hero-blob arf-hero-blob-1" />
            <span className="arf-hero-blob arf-hero-blob-2" />

            {/* Motes drifting up through the banner */}
            <span className="arf-motes" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <span className="arf-mote" key={i} />
              ))}
            </span>

            <Card.Body className="position-relative p-4 p-md-5 d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <h1 className="fw-bold mb-1 arf-hero-title">
                  <span className="arf-hero-icon">💰</span>
                  <span className="arf-hero-title-text">Refund Requests</span>
                </h1>
                <p className="mb-0 arf-hero-text">
                  Review and process customer refund requests
                </p>
              </div>

              <div className="arf-stats">
                <div className="arf-stat">
                  <span className="arf-stat-value">{refunds.length}</span>
                  <span className="arf-stat-label">Total</span>
                </div>

                <div className="arf-stat is-pending">
                  <span className="arf-stat-value">{countOf('Pending')}</span>
                  <span className="arf-stat-label">Pending</span>
                </div>

                <div className="arf-stat is-approved">
                  <span className="arf-stat-value">{countOf('Approved')}</span>
                  <span className="arf-stat-label">Approved</span>
                </div>

                <div className="arf-stat is-paid">
                  <span className="arf-stat-value">{countOf('Refunded')}</span>
                  <span className="arf-stat-label">Refunded</span>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Search */}
          <div className="arf-search-row">
            <div className="arf-search">
              <span className="arf-search-icon" aria-hidden="true">
                🔍
              </span>

              <Form.Control
                className="arf-search-input"
                placeholder="Search by customer, product or order..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {search && (
                <button
                  type="button"
                  className="arf-search-clear"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                >
                  ×
                </button>
              )}
            </div>

            <span className="arf-result-count">
              <span className="arf-result-num">{filteredRefunds.length}</span>
              shown
            </span>
          </div>

          {/* Status filters */}
          <div className="arf-chips">
            {filters.map((key) => {
              const meta = key === 'All' ? null : stateOf(key);

              return (
                <button
                  key={key}
                  type="button"
                  className={`arf-chip${statusFilter === key ? ' is-active' : ''}`}
                  style={{ '--chip': meta ? meta.solid : '#9a3412' }}
                  onClick={() => setStatusFilter(key)}
                >
                  {meta ? `${meta.icon} ${key}` : '✨ All'}
                  <span className="arf-chip-count">{countOf(key)}</span>
                </button>
              );
            })}
          </div>

          {/* Refund cards */}
          {filteredRefunds.length === 0 ? (
            <Card
              className="border-0 shadow-sm text-center py-5"
              style={{ borderRadius: '18px' }}
            >
              <Card.Body>
                <div className="arf-empty-icon">💸</div>
                <h5 className="fw-bold mt-2">No Refund Requests Found</h5>
                <p className="text-muted mb-0">
                  Try a different search or filter.
                </p>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {filteredRefunds.map((refund, index) => {
                const state = stateOf(refund.status);

                /* Only a pending request is still costing the customer time */
                const waitingDays = Math.floor(
                  (Date.now() - new Date(refund.createdAt).getTime()) / DAY
                );
                const showWait = refund.status === 'Pending';

                const deleteIn = daysUntilDelete(refund);

                return (
                  <Col xl={4} lg={6} md={6} xs={12} key={refund._id}>
                    <Card
                      className="border-0 shadow-sm h-100 arf-card"
                      style={{
                        '--r-from': state.from,
                        '--r-to': state.to,
                        '--r-accent': state.solid,
                        /* capped so late cards don't wait seconds */
                        '--i': Math.min(index, 11)
                      }}
                    >
                      <span className="arf-card-strip" aria-hidden="true" />

                      <Card.Body className="d-flex flex-column p-4">
                        <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                          <span className="arf-order">
                            {refund.orderNumber || 'N/A'}
                          </span>

                          <span className="arf-status-pill">
                            <span
                              className="arf-status-dot"
                              aria-hidden="true"
                            />
                            {state.icon} {refund.status}
                          </span>
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-3">
                          <span className="arf-avatar">
                            {refund.userName?.charAt(0).toUpperCase() || '?'}
                          </span>
                          <div className="text-truncate">
                            <div className="fw-semibold text-truncate arf-name">
                              {refund.userName || 'N/A'}
                            </div>
                            <small className="text-muted text-truncate d-block">
                              📦 {refund.productName || 'N/A'}
                            </small>
                          </div>
                        </div>

                        <div className="arf-detail mb-2">
                          <div className="arf-label">Reason</div>
                          <div className="fw-semibold">{refund.reason}</div>
                        </div>

                        {refund.description && (
                          <div className="arf-detail mb-3">
                            <div className="arf-label">Description</div>
                            <p className="mb-0 arf-desc">
                              {refund.description}
                            </p>
                          </div>
                        )}

                        {refund.images && refund.images.length > 0 && (
                          <div className="arf-detail mb-3">
                            <div className="arf-label">
                              Photos ({refund.images.length})
                            </div>
                            <div className="d-flex flex-wrap gap-2 mt-1">
                              {refund.images.map((img, i) => (
                                <a
                                  key={i}
                                  href={img}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Open full size"
                                  className="arf-evidence-link"
                                >
                                  <img
                                    src={img}
                                    alt={`evidence ${i + 1}`}
                                    className="arf-evidence"
                                  />
                                  <span
                                    className="arf-evidence-zoom"
                                    aria-hidden="true"
                                  >
                                    🔍
                                  </span>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                          <small className="text-muted">
                            📅 {new Date(refund.createdAt).toLocaleDateString()}
                          </small>

                          {showWait && (
                            <span className={`arf-wait ${ageToneOf(waitingDays)}`}>
                              ⏱{' '}
                              {waitingDays === 0
                                ? 'Today'
                                : `${waitingDays}d waiting`}
                            </span>
                          )}
                        </div>

                        {deleteIn !== null && (
                          <div
                            className={`arf-autodelete mb-3${
                              deleteIn <= 1 ? ' is-urgent' : ''
                            }`}
                          >
                            🗑{' '}
                            {deleteIn === 0
                              ? 'Auto-deletes today'
                              : `Auto-deletes in ${deleteIn} day${deleteIn > 1 ? 's' : ''}`}
                          </div>
                        )}

                        <div className="arf-actions mt-auto">
                          {DECISIONS.map((d) => {
                            const isCurrent = refund.status === d.status;
                            const allowed = canTransition(
                              refund.status,
                              d.status
                            );

                            return (
                              <button
                                key={d.status}
                                type="button"
                                className={`arf-act arf-act-${d.key}${
                                  isCurrent ? ' is-current' : ''
                                }`}
                                disabled={!allowed}
                                title={
                                  isCurrent
                                    ? `Already ${d.status.toLowerCase()}`
                                    : allowed
                                      ? `Mark as ${d.status}`
                                      : `A ${refund.status.toLowerCase()} refund cannot be ${d.status.toLowerCase()}`
                                }
                                onClick={() =>
                                  updateStatus(refund._id, d.status)
                                }
                              >
                                {isCurrent ? d.done : d.idle}
                              </button>
                            );
                          })}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </div>

      <style>{refundsStyles}</style>
    </>
  );
}

const refundsStyles = `
  .arf-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 48px) 0;
  }

  /* ===== Loading ===== */
  .arf-loader {
    position: relative;
    width: 88px;
    height: 88px;
    margin: 40px auto 0;
  }
  .arf-loader-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 3px solid rgba(245, 158, 11, 0.22);
    border-top-color: #ea580c;
    border-right-color: #f59e0b;
    animation: arfSpin 1s linear infinite;
  }
  @keyframes arfSpin {
    to { transform: rotate(360deg); }
  }
  .arf-loader-coin {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    animation: arfIconBob 2.4s ease-in-out infinite;
  }
  .arf-loader-text {
    margin-top: 18px;
    font-weight: 700;
    color: #9a3412;
  }

  /* ===== Sanctum banner ===== */
  .arf-hero {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    border-radius: 22px;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #3b0764 30%, #7c2d12 64%, #ea580c);
    background-size: 220% 220%;
    border: 1px solid rgba(253, 230, 138, 0.25) !important;
    animation: arfHeroDrift 18s ease-in-out infinite alternate;
  }
  @keyframes arfHeroDrift {
    from { background-position: 0% 50%; }
    to   { background-position: 100% 50%; }
  }
  .arf-hero::before,
  .arf-hero::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 2px;
    z-index: 3;
    background: linear-gradient(90deg, transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 220% 100%;
    animation: arfThread 3.6s linear infinite;
  }
  .arf-hero::before { top: 0; }
  .arf-hero::after { bottom: 0; animation-direction: reverse; }
  @keyframes arfThread {
    to { background-position: 220% 0; }
  }

  .arf-hero-aurora {
    position: absolute;
    inset: -40%;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(36% 44% at 24% 30%, rgba(251,191,36,0.40), transparent 62%),
      radial-gradient(32% 40% at 78% 26%, rgba(34,197,94,0.30), transparent 62%),
      radial-gradient(40% 46% at 58% 84%, rgba(234,88,12,0.38), transparent 62%);
    filter: blur(30px);
    animation: arfAurora 22s ease-in-out infinite alternate;
    will-change: transform;
  }
  @keyframes arfAurora {
    0%   { transform: translate3d(-4%, -3%, 0) rotate(0deg)  scale(1.05); }
    50%  { transform: translate3d(5%, 4%, 0)   rotate(8deg)  scale(1.18); }
    100% { transform: translate3d(-3%, 5%, 0)  rotate(-6deg) scale(1.1); }
  }

  .arf-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.45;
    pointer-events: none;
    z-index: 0;
  }
  .arf-hero-blob-1 {
    width: 200px; height: 200px;
    background: #f59e0b;
    top: -80px; left: 20%;
  }
  .arf-hero-blob-2 {
    width: 180px; height: 180px;
    background: #22c55e;
    bottom: -90px; right: 8%;
    opacity: 0.3;
  }

  .arf-motes { position: absolute; inset: 0; z-index: 1; pointer-events: none; overflow: hidden; }
  .arf-mote {
    position: absolute;
    bottom: -12px;
    width: 6px; height: 6px;
    border-radius: 50%;
    background: rgba(253, 230, 138, 0.9);
    box-shadow: 0 0 12px rgba(251, 191, 36, 0.9);
    opacity: 0;
    animation: arfMote 12s linear infinite;
  }
  .arf-mote:nth-child(1) { left: 7%;  animation-delay: 0s; }
  .arf-mote:nth-child(2) { left: 20%; animation-delay: 4.3s; width: 4px; height: 4px; }
  .arf-mote:nth-child(3) { left: 33%; animation-delay: 1.7s; }
  .arf-mote:nth-child(4) { left: 46%; animation-delay: 7.2s; width: 5px; height: 5px; }
  .arf-mote:nth-child(5) { left: 59%; animation-delay: 2.8s; }
  .arf-mote:nth-child(6) { left: 72%; animation-delay: 9.2s; width: 4px; height: 4px; }
  .arf-mote:nth-child(7) { left: 85%; animation-delay: 5.6s; }
  .arf-mote:nth-child(8) { left: 95%; animation-delay: 3.4s; width: 5px; height: 5px; }
  @keyframes arfMote {
    0%   { opacity: 0; transform: translateY(0) translateX(0) scale(0.6); }
    12%  { opacity: 1; }
    80%  { opacity: 0.6; }
    100% { opacity: 0; transform: translateY(-230px) translateX(22px) scale(1.1); }
  }

  .arf-hero .card-body { z-index: 2; }
  .arf-hero-title { display: flex; align-items: center; gap: 10px; }
  .arf-hero-icon {
    display: inline-block;
    animation: arfIconBob 3.2s ease-in-out infinite;
  }
  @keyframes arfIconBob {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-6px) rotate(-8deg); }
  }
  .arf-hero-title-text {
    background: linear-gradient(100deg,
      #ffffff 0%, #ffffff 32%, #fde68a 46%, #fbbf24 52%, #fde68a 58%, #ffffff 74%, #ffffff 100%);
    background-size: 250% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: arfTitleShine 5.5s ease-in-out infinite;
  }
  @keyframes arfTitleShine {
    0%, 100% { background-position: 130% 0; }
    50%      { background-position: -30% 0; }
  }
  .arf-hero-text { opacity: 0.85; }

  .arf-stats { display: flex; flex-wrap: wrap; gap: 9px; }
  .arf-stat {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    min-width: 80px;
    padding: 9px 15px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(253, 230, 138, 0.3);
    backdrop-filter: blur(6px);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      background 0.25s ease, border-color 0.25s ease;
  }
  .arf-stat:hover {
    transform: translateY(-3px);
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(253, 230, 138, 0.65);
  }
  .arf-stat-value { font-size: 1.3rem; font-weight: 800; line-height: 1.1; }
  .arf-stat-label {
    font-size: 0.63rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.78;
    margin-top: 2px;
  }
  .arf-stat.is-pending .arf-stat-value  { color: #fcd34d; }
  .arf-stat.is-approved .arf-stat-value { color: #6ee7b7; }
  .arf-stat.is-paid .arf-stat-value     { color: #93c5fd; }
  /* Pending requests are people still waiting on their money */
  .arf-stat.is-pending { animation: arfAlert 2.4s ease-in-out infinite; }
  @keyframes arfAlert {
    0%, 100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.5); }
    50%      { box-shadow: 0 0 0 7px rgba(251, 191, 36, 0); }
  }

  /* ===== Search ===== */
  .arf-search-row {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }
  .arf-search {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1 1 320px;
    max-width: 520px;
    padding: 4px 14px;
    border-radius: 50px;
    background: #fff;
    border: 2px solid rgba(245, 158, 11, 0.26);
    box-shadow: 0 10px 28px rgba(194, 65, 12, 0.1);
    transition: border-color 0.25s ease, box-shadow 0.25s ease;
  }
  .arf-search:focus-within {
    border-color: #f59e0b;
    box-shadow: 0 14px 36px rgba(245, 158, 11, 0.28);
  }
  .arf-search-icon { flex: none; font-size: 1rem; }
  .arf-search-input {
    border: none !important;
    padding: 9px 0;
    box-shadow: none !important;
    background: transparent;
  }
  .arf-search-clear {
    flex: none;
    border: none;
    width: 22px; height: 22px;
    border-radius: 50%;
    line-height: 1;
    font-size: 1.05rem;
    color: #b45309;
    background: #fff7ed;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease;
  }
  .arf-search-clear:hover { background: #fde68a; transform: rotate(90deg); }
  .arf-result-count {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #9a3412;
  }
  .arf-result-num {
    font-weight: 800;
    color: #fff;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    border-radius: 30px;
    padding: 2px 11px;
    box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
  }

  /* ===== Status chips ===== */
  .arf-chips { display: flex; flex-wrap: wrap; gap: 9px; margin-bottom: 22px; }
  .arf-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 9px 7px 15px;
    border-radius: 50px;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.75);
    border: 1px solid var(--chip, #f59e0b);
    color: var(--chip, #9a3412);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      background 0.25s ease, color 0.25s ease, box-shadow 0.25s ease;
  }
  .arf-chip:hover { transform: translateY(-2px); }
  .arf-chip:active { transform: translateY(0) scale(0.96); }
  .arf-chip.is-active {
    color: #fff;
    background: var(--chip, #ea580c);
    animation: arfChipPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @keyframes arfChipPop {
    0%   { transform: scale(0.88); }
    55%  { transform: scale(1.08); }
    100% { transform: scale(1); }
  }
  .arf-chip-count {
    min-width: 22px;
    padding: 1px 8px;
    border-radius: 30px;
    font-size: 0.72rem;
    font-weight: 800;
    background: #fff7ed;
    color: var(--chip, #9a3412);
  }
  .arf-chip.is-active .arf-chip-count { background: rgba(255, 255, 255, 0.92); }

  /* ===== Refund cards ===== */
  .arf-card {
    position: relative;
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid rgba(245, 158, 11, 0.14) !important;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    animation: arfCardIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
    animation-delay: calc(var(--i, 0) * 60ms);
  }
  @keyframes arfCardIn {
    from { opacity: 0; transform: translateY(20px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  /* The old 5px inline border, but alive */
  .arf-card-strip {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 5px;
    background: linear-gradient(90deg,
      var(--r-from, #ea580c), var(--r-to, #f59e0b), var(--r-from, #ea580c));
    background-size: 220% 100%;
    animation: arfStrip 3.4s linear infinite;
  }
  @keyframes arfStrip {
    to { background-position: 220% 0; }
  }
  .arf-card:hover {
    transform: translateY(-7px);
    box-shadow: 0 24px 42px color-mix(in srgb, var(--r-accent, #ea580c) 28%, transparent) !important;
  }
  @supports not (box-shadow: 0 0 0 color-mix(in srgb, red 10%, blue)) {
    .arf-card:hover { box-shadow: 0 22px 40px rgba(194, 65, 12, 0.18) !important; }
  }

  .arf-order {
    font-weight: 800;
    color: #94a3b8;
    font-size: 0.85rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  }

  .arf-status-pill {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 12px;
    border-radius: 30px;
    font-size: 0.72rem;
    font-weight: 700;
    white-space: nowrap;
    color: #fff;
    background: var(--r-accent, #ea580c);
  }
  .arf-status-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #fff;
    animation: arfDot 1.7s ease-in-out infinite;
  }
  @keyframes arfDot {
    0%, 100% { opacity: 0.4; transform: scale(0.8); }
    50%      { opacity: 1;   transform: scale(1.35); }
  }

  .arf-avatar {
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #fff;
    font-weight: 700;
    background: linear-gradient(135deg, var(--r-from, #ea580c), var(--r-to, #f59e0b));
    box-shadow: 0 5px 14px color-mix(in srgb, var(--r-accent, #ea580c) 42%, transparent);
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @supports not (box-shadow: 0 0 0 color-mix(in srgb, red 10%, blue)) {
    .arf-avatar { box-shadow: 0 5px 14px rgba(124, 45, 18, 0.28); }
  }
  .arf-card:hover .arf-avatar { transform: scale(1.09) rotate(-5deg); }
  .arf-name { color: #1f2937; transition: color 0.25s ease; }
  .arf-card:hover .arf-name { color: var(--r-accent, #ea580c); }

  .arf-detail {
    background: #fffdf7;
    border: 1px solid #f6eee2;
    border-radius: 12px;
    padding: 9px 13px;
    transition: border-color 0.25s ease, background 0.25s ease;
  }
  .arf-card:hover .arf-detail { background: #fff; }
  .arf-label {
    font-size: 0.66rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #b45309;
  }
  .arf-desc {
    font-size: 0.88rem;
    color: #475569;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* Evidence thumbnails */
  .arf-evidence-link {
    position: relative;
    display: inline-block;
    line-height: 0;
    border-radius: 12px;
    overflow: hidden;
  }
  .arf-evidence {
    width: 62px;
    height: 62px;
    object-fit: cover;
    border-radius: 12px;
    border: 1px solid #f2e3cd;
    cursor: pointer;
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease;
  }
  .arf-evidence-link:hover .arf-evidence {
    transform: scale(1.08);
    box-shadow: 0 8px 20px rgba(124, 45, 18, 0.3);
  }
  .arf-evidence-zoom {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    border-radius: 12px;
    background: rgba(17, 24, 39, 0.45);
    opacity: 0;
    transition: opacity 0.25s ease;
  }
  .arf-evidence-link:hover .arf-evidence-zoom { opacity: 1; }

  /* ===== Waiting time ===== */
  .arf-wait {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 10px;
    border-radius: 30px;
    font-size: 0.72rem;
    font-weight: 700;
    border: 1px solid transparent;
  }
  .arf-wait.is-cool {
    color: #0e7490;
    background: #ecfeff;
    border-color: #a5f3fc;
  }
  .arf-wait.is-warm {
    color: #b45309;
    background: #fffbeb;
    border-color: #fcd34d;
  }
  .arf-wait.is-hot {
    color: #b91c1c;
    background: #fef2f2;
    border-color: #fecaca;
    animation: arfWaitAlert 2s ease-in-out infinite;
  }
  @keyframes arfWaitAlert {
    0%, 100% { box-shadow: 0 0 0 0 rgba(248, 113, 113, 0.5); }
    50%      { box-shadow: 0 0 0 5px rgba(248, 113, 113, 0); }
  }

  /* ===== Decision row ===== */
  .arf-actions {
    display: flex;
    gap: 8px;
  }
  .arf-act {
    flex: 1 1 0;
    min-width: 0;
    border-radius: 12px;
    padding: 8px 6px;
    font-size: 0.79rem;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    background: #fff;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease, background 0.25s ease, color 0.25s ease,
      border-color 0.25s ease;
  }
  .arf-act:hover:not(:disabled) { transform: translateY(-2px); }
  .arf-act:active:not(:disabled) { transform: translateY(0) scale(0.96); }
  /* A decision that is taken stays filled; one that is no longer allowed
     fades back so it reads as closed off rather than merely idle. */
  .arf-act:disabled { cursor: not-allowed; }
  .arf-act:disabled:not(.is-current) {
    opacity: 0.4;
    border-color: #e7e5e4;
    color: #a8a29e;
    background: #fafaf9;
  }

  /* Countdown to auto-deletion */
  .arf-autodelete {
    display: inline-block;
    align-self: flex-start;
    font-size: 0.78rem;
    font-weight: 600;
    color: #b45309;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 30px;
    padding: 5px 12px;
  }
  .arf-autodelete.is-urgent {
    color: #b91c1c;
    background: #fef2f2;
    border-color: #fecaca;
    animation: arfWaitAlert 1.8s ease-in-out infinite;
  }

  .arf-act-ok { border: 1px solid #86efac; color: #15803d; }
  .arf-act-ok:hover:not(:disabled) {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #065f46, #16a34a 55%, #4ade80);
    box-shadow: 0 10px 22px rgba(22, 163, 74, 0.4);
  }
  .arf-act-ok.is-current {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #065f46, #16a34a 55%, #4ade80);
  }

  .arf-act-no { border: 1px solid #fecaca; color: #b91c1c; }
  .arf-act-no:hover:not(:disabled) {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #991b1b, #dc2626 55%, #ef4444);
    box-shadow: 0 10px 22px rgba(220, 38, 38, 0.4);
  }
  .arf-act-no.is-current {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #991b1b, #dc2626 55%, #ef4444);
  }

  .arf-act-paid { border: 1px solid #bfdbfe; color: #1d4ed8; }
  .arf-act-paid:hover:not(:disabled) {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #1e3a8a, #2563eb 55%, #60a5fa);
    box-shadow: 0 10px 22px rgba(37, 99, 235, 0.4);
  }
  .arf-act-paid.is-current {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #1e3a8a, #2563eb 55%, #60a5fa);
  }

  .arf-empty-icon {
    font-size: 3rem;
    animation: arfIconBob 3s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .arf-hero,
    .arf-hero::before,
    .arf-hero::after,
    .arf-hero-aurora,
    .arf-mote,
    .arf-hero-icon,
    .arf-hero-title-text,
    .arf-stat.is-pending,
    .arf-chip.is-active,
    .arf-card,
    .arf-card-strip,
    .arf-status-dot,
    .arf-wait.is-hot,
    .arf-autodelete.is-urgent,
    .arf-loader-coin,
    .arf-empty-icon { animation: none !important; }
    .arf-motes { display: none; }
    .arf-hero-title-text {
      -webkit-text-fill-color: #fff;
      color: #fff;
    }
  }
`;

export default AdminRefundsPage;
